import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { track, COMPANY, PHONE_DISPLAY, PHONE_TEL, resetCookieConsent } from "@/lib/api";

export default function Footer() {
  return (
    <footer className="bg-ink border-t border-white/10 py-16">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="font-display text-3xl mb-4">
              <span className="text-white">ZATEPLENIA</span><span className="text-gold">.EU</span>
            </div>
            <p className="text-white/50 text-sm max-w-xs leading-relaxed">
              Online kalkulačka zateplenia rodinných domov, bytových domov a budov.
              Materiál, práca, lešenie a doprava — do 60 sekúnd.
            </p>
            <div className="mt-6 text-white/45 text-xs leading-relaxed" data-testid="footer-company">
              <p className="text-white/70 font-semibold">{COMPANY.name}</p>
              <p>{COMPANY.street}, {COMPANY.city}</p>
              <p>IČO: {COMPANY.ico} · DIČ: {COMPANY.dic}</p>
              <p>IČ DPH: {COMPANY.icdph}</p>
              <p>Zapísaná v OR {COMPANY.court}, oddiel: {COMPANY.section}, vložka č. {COMPANY.insert}</p>
            </div>
          </div>
          <div>
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Kontakt</p>
            <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone" className="flex items-center gap-2 text-gold hover:text-gold-light transition-colors font-bold text-base mb-2">
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
            <p className="text-white/70 text-sm">{COMPANY.email}</p>
            <p className="text-white/70 text-sm">Slovensko</p>
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-3 mt-6">Ďalšie stavebné práce</p>
            <a href="https://stavebne-dopyty.sk/pridat-dopyt" target="_blank" rel="noreferrer" data-testid="footer-sd-link"
              onClick={() => track("sd_click", "footer")}
              className="text-white/70 text-sm hover:text-gold transition-colors block">Zadať dopyt na Stavebné-dopyty.sk →</a>
          </div>
          <div>
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Zateplenie v mestách</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-white/70">
              {[
                ["bratislava", "Bratislava"], ["kosice", "Košice"], ["presov", "Prešov"],
                ["zilina", "Žilina"], ["nitra", "Nitra"], ["banska-bystrica", "Banská Bystrica"],
                ["trnava", "Trnava"], ["trencin", "Trenčín"], ["poprad", "Poprad"],
                ["martin", "Martin"], ["prievidza", "Prievidza"], ["nove-zamky", "Nové Zámky"],
              ].map(([slug, name]) => (
                <a key={slug} href={`/zateplenie/${slug}`} className="hover:text-gold transition-colors">Zateplenie {name}</a>
              ))}
            </div>
            <div className="flex flex-col gap-2 text-sm text-white/70 mt-4 pt-4 border-t border-white/10">
              <a href="/#kalkulacka" data-testid="footer-calc-link" className="hover:text-gold transition-colors">Kalkulačka cien zateplenia</a>
              <a href="/blog" className="hover:text-gold transition-colors">Blog o zateplení</a>
              <a href="/faq" data-testid="footer-faq-link" className="hover:text-gold transition-colors">Časté otázky (FAQ)</a>
              <a href="/normy" data-testid="footer-normy-link" className="hover:text-gold transition-colors">Zákony a normy 2026</a>
              <a href="/kontakt" data-testid="footer-kontakt-link" className="hover:text-gold transition-colors">Kontakt</a>
              <a href="/okresy" data-testid="footer-okresy-link" className="hover:text-gold transition-colors">Zateplenie podľa okresov</a>
              <a href="/materialy" data-testid="footer-materials-link" className="hover:text-gold transition-colors">Porovnanie materiálov</a>
              <a href="/omietky" data-testid="footer-plasters-link" className="hover:text-gold transition-colors">Porovnanie omietok</a>
              <a href="/podhlady" data-testid="footer-podhlady-link" className="hover:text-gold transition-colors">Zateplenie podhľadu</a>
              <a href="/objednavka" data-testid="footer-order-link" className="hover:text-gold transition-colors">Objednávka zateplenia</a>
              <a href="/galeria" data-testid="footer-gallery-link" className="hover:text-gold transition-colors">Galéria vizualizácií</a>
              <a href="/premeny" data-testid="footer-premeny-link" className="hover:text-gold transition-colors">Premeny pred/po</a>
              <a href="/partneri" className="hover:text-gold transition-colors">Naši partneri</a>
              <a href="/realizatori" data-testid="footer-firms-catalog-link" className="hover:text-gold transition-colors">Katalóg realizátorov</a>
              <a href="/stat-sa-partnerom" className="hover:text-gold transition-colors">Staňte sa partnerom</a>
              <a href="/firma-portal" data-testid="footer-firm-portal-link" className="hover:text-gold transition-colors">Ste realizačná firma? Registrujte sa</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between gap-3 text-white/40 text-xs font-mono2 uppercase tracking-widest">
          <span>© {new Date().getFullYear()} {COMPANY.name}</span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/ochrana-osobnych-udajov" data-testid="footer-gdpr-link" className="hover:text-gold transition-colors">Ochrana údajov</Link>
            <Link to="/obchodne-podmienky" data-testid="footer-terms-link" className="hover:text-gold transition-colors">Obchodné podmienky</Link>
            <Link to="/reklamacny-poriadok" data-testid="footer-complaints-link" className="hover:text-gold transition-colors">Reklamačný poriadok</Link>
            <Link to="/pouzivanie-cookies" data-testid="footer-cookies-link" className="hover:text-gold transition-colors">Cookies</Link>
            <button onClick={resetCookieConsent} data-testid="footer-cookie-settings" className="uppercase tracking-widest hover:text-gold transition-colors">Nastavenia cookies</button>
            <a href="/partner" className="hover:text-gold transition-colors">Partner portál</a>
            <a href="/admin" data-testid="footer-admin-link" className="hover:text-gold transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
